import styled from "@emotion/styled";
import theme from "../../theme";
import useGetNoticeDetail from "../../hooks/useGetNoticeDetail";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AlertModal from "../../components/alertModal";
import { useState, useEffect } from "react";
import {
  BackButton,
  ExclamationMark,
  WarningIcon,
} from "../../assets/icons/icons";
import useDeleteNotice from "../../hooks/useDeleteNotice";
import userStorage from "../../utils/userStorage";
import ReactQuill from "react-quill";
import Spinner from "../../components/spinner";
import { usePutReadingCheck } from "../../hooks/usePutReadingCheck";
import useGetNotice from "../../hooks/useGetNotice";
import {
  Notice,
  NoticeDetail as NoticeDetailType,
} from "../../types/types";
import { Button } from "../../components/common/Button";
import { deleteNoticeImage } from "../../api/notice";
import { PageLayout } from "../../components/PageLayout";
import { useWindowSize } from "@reactuses/core";
import { formatDate, handleKeyDown } from "../../utils/util";
import { useI18n } from "../../locales";

const { md } = theme.screen;
const modules = {
  toolbar: false,
};

const NoticeDetail: React.FC<{}> = () => {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const user = userStorage.get();
  const { width } = useWindowSize();
  const { data, isLoading } = useGetNoticeDetail({
    idx: Number(id),
    isAdmin: user?.permission?.auth_notice === "Y",
  });
  const [openDelModal, setOpenDelModal] = useState(false);
  const navigate = useNavigate();

  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckClick, setIsCheckClick] = useState(false);
  const [prevNotice, setPrevNotice] = useState<Notice>();
  const [nextNotice, setNextNotice] = useState<Notice>();
  const [clickCount, setClickCount] = useState(0); // 목록으로 돌아가기용 클릭 카운터
  const [isFromHome, setIsFromHome] = useState(false); // 홈에서 자세히보기 버튼을 클릭하여 접근했는지 확인

  const { mutate: deletePost, isPending } = useDeleteNotice({
    idx: id as string,
  });

  const { mutate: patchReadingCheck } = usePutReadingCheck({
    idx: id ? id : "",
  });
  const { data: allNoticeData } = useGetNotice(0, 1, -1); //(page, newCheck, limit) 공지리스트 전체 조회
  const { data: notReadNoticeData } = useGetNotice(0, 0, -1); // 안 읽은 공지

  const onPressNo = () => {
    setOpenDelModal(false);
  };

  const onPressYes = () => {
    if (data) {
      deleteNoticeImageFn(data);
    }
    deletePost();
  };

  // 목록으로 돌아가기
  const goBack = () => {
    if (isFromHome) {
      navigate(`/notice`);
    } else {
      if (clickCount === 0) {
        navigate(-1);
      } else {
        navigate(clickCount - 1);
      }
    }
  };

  const goToModify = () => {
    // 수정하기
    navigate(`/notice-admin`, {
      state: {
        data,
        id,
      },
    });
  };

  // 확인 버튼 클릭
  const handleClickCheck = () => {
    patchReadingCheck();
    setIsCheckClick(true);
  };

  // 이전, 다음 공지 버튼 클릭
  const handleClickPrevNext = (idx: number) => {
    navigate(`/notice/${idx}`);
    setClickCount((prev) => prev - 1);
  };

  // 공지의 이미지를 삭제하는 함수
  const deleteNoticeImageFn = async (data: NoticeDetailType) => {
    let contents = data.contents;
    // 컨텐츠에 이미지 여부와 갯수 정의
    let imgCounter = contents.match(/<img/g);

    if (imgCounter && imgCounter.length === 0) {
      // 이미지가 0개
      return;
    } else if (imgCounter && imgCounter.length > 0) {
      // 이미지가 있음
      for (let i = 0; i < imgCounter.length; i++) {
        // let deleteDataFirstIndex = contents.indexOf("<img");
        let deleteDataLastIndex = contents.indexOf('">') + 2;

        let deleteImageName = contents.substring(
          contents.indexOf("userimg/") + 8,
          contents.indexOf('">')
        );

        const res = await deleteNoticeImage({
          fileName: deleteImageName,
        });
        if (res.httpStatus === "OK") {
          // console.log("이미지 삭제 성공");
          // 컨텐츠에서 지운 이미지 글자 제외시키기
          contents = contents.substring(deleteDataLastIndex);
          // console.log("수정된 컨텐츠", contents);
        } else {
          // console.log("삭제 오류", res);
        }
      }
    }
  };

  // 로케이션 경로 확인
  useEffect(() => {
    if (location && location.state && location.state.from === "home") {
      setIsFromHome(true);
    }
  }, [location]);

  useEffect(() => {
    // 공지사항 수정/삭제 권한 : 로그인 유저의 grade 가 2이거나 로그인 유저가 작성한 경우
    if (user && data) {
      if (user.grade === 2 || user.idx === Number(data.writer_idx)) {
        setIsAdmin(true);
      }
    }
  }, [user, data]);

  // 이전값, 다음값 세팅하기
  useEffect(() => {
    setPrevNotice(undefined);
    setNextNotice(undefined);
    if (id && allNoticeData) {
      let currentIndex = allNoticeData.list.findIndex(
        (el) => el.idx === Number(id)
      ); // 현재 보여지는 공지의 인덱스
      if (currentIndex !== 0) {
        // 첫 값이 아닐때
        setPrevNotice(allNoticeData.list[currentIndex - 1]);
      }
      if (currentIndex !== allNoticeData.list.length - 1) {
        // 끝 값이 아닐때
        setNextNotice(allNoticeData.list[currentIndex + 1]);
      }
    }
  }, [id, allNoticeData]);

  return (
    <PageLayout
      bodyHeight="calc(100vh - 112px)"
      bodyMinHeight="1105px"
      withBreadcrumb
    >
      <Title>{t.notice.title}</Title>
      {isLoading && <Spinner />}
      {data && (
        <NoticeListWrapper>
          <NoticeTitle>{data?.title}</NoticeTitle>
          <NoticeDate>
            {data && formatDate(data?.create_dt, "yyyy-MM-dd HH:mm:ss")}
          </NoticeDate>
        </NoticeListWrapper>
      )}
      {data && (
        <DetailWrapper>
          <Detail
            theme="snow"
            modules={modules}
            readOnly={true}
            value={data?.contents}
          />
          <BtnWrapper>
            <BtnLeftWrapper>
              <Button
                bgColor={theme.statusColor.CLOSE}
                hoverColor={theme.statusColor.CLOSE_HOVER}
                onClick={goBack}
                style={{
                  width: width <= md ? `50px` : "137px",
                  height: "40px",
                  fontSize: "14px",
                }}
              >
                {width <= md ? (
                  <BackButton
                    width="25px"
                    height="25px"
                    color={theme.color.white}
                  />
                ) : (
                  t.notice.goBack
                )}
              </Button>

              {/* 확인 버튼 */}
              {!isCheckClick &&
                (data?.reader_list == null ||
                  !data?.reader_list.includes(
                    "'" + String(user?.idx) + "'"
                  )) && (
                  <Button
                    bgColor={theme.statusColor.MAIN}
                    hoverColor={theme.statusColor.MAIN_HOVER}
                    onClick={() => {
                      handleClickCheck();
                    }}
                    style={{
                      width: width <= md ? `50px` : "56px",
                      height: "40px",
                      fontSize: width <= md ? `12px` : "14px",
                    }}
                  >
                    {t.notice.confirm}
                  </Button>
                )}
            </BtnLeftWrapper>

            {isAdmin && (
              <BtnRightWrapper>
                <Button
                  bgColor={theme.color.aquamarine_500}
                  onClick={goToModify}
                  hoverColor={theme.color.aquamarine_600}
                  style={{
                    width: width <= md ? `50px` : "137px",
                    height: "40px",
                    fontSize: width <= md ? `12px` : "14px",
                  }}
                  isDisabled={isPending}
                >
                  {width <= md ? t.notice.edit : t.notice.editContent}
                </Button>
                <Button
                  bgColor={theme.statusColor.CLOSE}
                  onClick={() => setOpenDelModal(true)}
                  hoverColor={theme.statusColor.CLOSE_HOVER}
                  style={{
                    width: width <= md ? `50px` : "137px",
                    height: "40px",
                    fontSize: width <= md ? `12px` : "14px",
                  }}
                  isDisabled={isPending}
                >
                  {width <= md ? t.notice.delete : t.notice.delete}
                </Button>
              </BtnRightWrapper>
            )}
          </BtnWrapper>
        </DetailWrapper>
      )}
      {/* 이전 공지 & 다음 공지 */}
      <PrevAndNextContainer>
        {prevNotice && (
          <ButtonContainer
            onClick={() => handleClickPrevNext(prevNotice.idx)}
            role="button"
            tabIndex={0}
            onKeyDown={handleKeyDown}
          >
            {notReadNoticeData &&
              notReadNoticeData.list.length > 0 &&
              notReadNoticeData.list.findIndex(
                (el) => el.idx === prevNotice.idx
              ) !== -1 && (
                <ExclamationMarkContainer>
                  <ExclamationMark />
                </ExclamationMarkContainer>
              )}
            <div
              style={{
                display: "flex",
                gap: width <= md ? "15px" : "30px",
                alignItems: "center",
              }}
            >
              <PrevButton>{t.notice.previousNotice}</PrevButton>
              <PrevNextTitle>{prevNotice.title}</PrevNextTitle>
            </div>
            <PrevNextDate>
              {width <= md
                ? formatDate(prevNotice.create_dt, "yyyy-MM-dd HH:mm:ss")
                : formatDate(prevNotice.create_dt, "yyyy-MM-dd HH:mm:ss")}
            </PrevNextDate>
          </ButtonContainer>
        )}
        {nextNotice && (
          <ButtonContainer
            onClick={() => handleClickPrevNext(nextNotice.idx)}
            role="button"
            tabIndex={0}
            onKeyDown={handleKeyDown}
          >
            {notReadNoticeData &&
              notReadNoticeData.list.length > 0 &&
              notReadNoticeData.list.findIndex(
                (el) => el.idx === nextNotice.idx
              ) !== -1 && (
                <ExclamationMarkContainer>
                  <ExclamationMark />
                </ExclamationMarkContainer>
              )}
            <div
              style={{
                display: "flex",
                gap: width <= md ? "15px" : "30px",
                alignItems: "center",
              }}
            >
              <NextButton>{t.notice.nextNotice}</NextButton>
              <PrevNextTitle>{nextNotice.title}</PrevNextTitle>
            </div>
            <PrevNextDate>
              {width <= md
                ? formatDate(nextNotice.create_dt, "yyyy-MM-dd HH:mm:ss")
                : formatDate(nextNotice.create_dt, "yyyy-MM-dd HH:mm:ss")}
            </PrevNextDate>
          </ButtonContainer>
        )}
      </PrevAndNextContainer>

      <AlertModal
        open={openDelModal}
        onPressNo={onPressNo}
        title={t.notice.deleteTitle}
        content={t.notice.deleteContent}
        confirmText={t.notice.delete}
        onPressYes={onPressYes}
        icon={<WarningIcon />}
      />
    </PageLayout>
  );
};

const Title = styled.h1`
  font-weight: 700;
  font-size: 20px;
  line-height: 18px;
  color: ${theme.color.neutral_600};
  padding-bottom: 15px;
  border-bottom: ${`3px solid ${theme.color.neutral_200}`};
  margin-bottom: 20px;
  padding-left: 10px;

  @media (max-width: ${md}px) {
    font-size: 18px;
  }
`;

const NoticeListWrapper = styled.div`
  background: ${theme.color.white};
  border-radius: 10px;
  padding: 10px;
  padding-left: 80px;
  padding-right: 80px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 10px;

  @media (max-width: ${md}px) {
    padding-left: 30px;
    padding-right: 30px;
    gap: 8px;
  }
`;

const NoticeTitle = styled.h1`
  font-size: 18px;
  line-height: 25px;
  color: ${theme.color.neutral_600};
  padding-top: 5px;
  padding-bottom: 5px;
  font-weight: bold;

  @media (max-width: ${md}px) {
    font-size: 15px;
    line-height: 22px;
    white-space: normal; /* 기본 줄 바꿈 동작 */
    word-wrap: break-word; /* 긴 단어를 줄 바꿈 */
    word-break: break-all; /* 모든 문자에서 줄 바꿈 가능 */
  }
`;

const NoticeDate = styled.p`
  font-weight: 400;
  font-size: 12px;
  line-height: 19px;
  color: ${theme.color.neutral_500};
  word-break: break-word;

  @media (max-width: ${md}px) {
    font-size: 11px;
    line-height: 18px;
    white-space: normal;
    word-break: break-word;
  }
`;

const DetailWrapper = styled.div`
  background-color: ${theme.color.white};
  flex: 1;
  border-radius: 10px;
  padding: 40px 80px 0px 80px;
  margin-bottom: 10px;
  min-height: 800px;

  @media (max-width: ${md}px) {
    padding: 20px 30px 0px 30px;
    min-height: calc(100vh - 207px);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`;

const Detail = styled(ReactQuill)`
  width: 100%;
  border-top: 1px solid ${theme.color.neutral_200};
  border-bottom: 1px solid ${theme.color.neutral_200};
  padding-top: 40px;
  max-height: calc(100% - 122px);
  min-height: calc(100% - 122px);
  overflow-y: auto;
  margin-bottom: 42px;
  .ql-editor strong {
    font-weight: 700;
  }
  .ql-editor em {
    font-style: italic;
  }
  .ql-editor u {
    text-decoration: underline;
  }
  .ql-editor {
    background-color: ${theme.color.white};
    border: none;
    border-radius: 10px;
    max-height: 567px;
    min-height: 350px;
  }
  .ql-container {
    border: none;
  }

  @media (max-width: ${md}px) {
    max-height: none;
    padding: 20px 0px;

    .ql-editor {
      max-height: none;
      padding: 0px;
    }
  }
`;

const BtnWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  @media (max-width: ${md}px) {
    padding-bottom: 20px;
  }
`;

const BtnLeftWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const BtnRightWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const PrevAndNextContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${theme.color.white};
  border-radius: 10px;
`;
const ButtonContainer = styled.div`
  position: relative;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: space-between;
  transition: 0.2s;
  border-radius: 10px;
  padding: 10px 80px;
  &:hover {
    background-color: ${theme.color.neutral_100};
  }
  @media (max-width: ${md}px) {
    padding: 10px 25px;
    gap: 10px;
  }
`;

const ExclamationMarkContainer = styled.div`
  position: absolute;
  left: 50px;

  @media (max-width: ${md}px) {
    left: 7px;
    svg {
      height: 15px;
      width: 15px;
    }
  }
`;

const PrevButton = styled.div`
  border: none;
  background-color: transparent;
  cursor: pointer;
  font-size: 15px;
  font-weight: bold;
  color: ${theme.color.neutral_600};
  padding: 0;
  flex-shrink: 0;

  @media (max-width: ${md}px) {
    font-size: 12px;
  }
`;
const NextButton = styled.div`
  border: none;
  background-color: transparent;
  cursor: pointer;
  font-size: 15px;
  font-weight: bold;
  color: ${theme.color.neutral_600};
  padding: 0;
  flex-shrink: 0;
  @media (max-width: ${md}px) {
    font-size: 12px;
  }
`;
const PrevNextTitle = styled.div`
  font-size: 16px;
  width: 900px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  @media (max-width: ${md}px) {
    font-size: 12px;
    width: calc(100vw - 170px);
  }
`;
const PrevNextDate = styled.div`
  font-size: 13px;
  color: ${theme.color.neutral_500};
  flex-shrink: 0;
  @media (max-width: ${md}px) {
    font-size: 10px;
  }
`;

export default NoticeDetail;
